import { LOGIN_CONTEXT } from "@constants/index";

export const rootPaths = {
  root: "/",
  dashboardRoot: "dashboard",
  projectRoot: "project",
  opensourceRoot: "open-source",
  statusRoot: "status",
  policyRoot: "policy",
  bulletinRoot: "bulletin",
  productRoot: "product",
  systemRoot: "system",
};

export default {
  dashboard: "/dashboard",
  projectList: "/projects",
  verificationReportList: "/verification-report",
  noticeInformationList: "/notice-information",
  importedComponentList: "/imported-component-list",
  verificationComparison: "/verification-comparison",
  sbomMerge: "/sbom-mergeds",
  previewsList: "/project-previews",
  componentList: "/components",
  licenseList: "/licenses",
  componentUsageStatus: "/component-usage-status",
  licenseUsageStatus: "/license-usage-status",
  vulnerabilityStatus: "/vulnerability-status",
  cveStatus: "/cve-status",
  componentCveStatus: "/component-cve-status",
  cveAnalysis: "/cve-browsing",
  cveAnalysisDetail: "/cve-browsing",
  licensePolicy: "/license-policy",
  vulnerabilityPolicy: "/vulnerability-policy",
  assetClassification: "/asset-classification",
  announcement: "/announcements",
  questionAndAnswer: "/question-and-answer",
  faq: "/faq",
  archive: "/archive",
  user: "/user",
  role: "/role",
  sbom: "/sbom",
  preferences: "/preferences",
  workHistory: "/work-history",
  product: "/products",
  group: "/group",
  cardLogout: "/logout",
  requestList: "/requests",
  fulfillmentProcess: "/fulfillment-process",
  userProfile: "/user/edit",
  sbomReadableView: "/sbom-readable-view",
};

const baseViewPermissions = [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER];
const baseEditPermissions = [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER];
const baseDeletePermissions = [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER];

// Route permissions configuration
export const routePermissions = {
  profile: {
    view: [...baseViewPermissions, LOGIN_CONTEXT.LICENSE_POLICY_EDIT, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT, LOGIN_CONTEXT.SBOM_EDIT],
  },
  sbomReadableView: {
    view: baseViewPermissions,
  },
  dashboard: {
    view: baseViewPermissions,
  },
  project: {
    projectList: {
      view: baseViewPermissions,
      add: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.MANAGER],
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.PRODUCT_MANAGER],
      delete: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.MANAGER],
    },
    verificationReportList: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
    noticeInformation: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
    verificationComparison: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.DEVELOPER, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
    sbomMerge: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.DEVELOPER, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
    previewsList: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.DEVELOPER, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],  
    },
    request: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.DEVELOPER, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
    importedComponent: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.DEVELOPER, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
  },
  
  openSource: {
    componentList: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      delete: baseDeletePermissions,
    },
    licenseList: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      delete: baseDeletePermissions,
    },
  },

  status: {
    componentUsageStatus: {
      view: baseViewPermissions,
      edit: baseEditPermissions,
      delete: baseDeletePermissions,
    },
    licenseUsageStatus: {
      view: baseViewPermissions,
      edit: baseEditPermissions,
      delete: baseDeletePermissions,
    },
    vulnerabilityStatus: {
      view: baseViewPermissions,
      edit: baseEditPermissions,
      delete: baseDeletePermissions,
    },
    cveStatus: {
      view: baseViewPermissions,
      edit: baseEditPermissions,
      delete: baseDeletePermissions,
    },
    cveAnalysis: {
      view: baseViewPermissions,
      edit: baseEditPermissions,
      delete: baseDeletePermissions,
    },
  },

  policy: {
    licensePolicy: {
      view: [...baseViewPermissions, LOGIN_CONTEXT.LICENSE_POLICY_EDIT],
      edit: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.LICENSE_POLICY_EDIT],
      delete: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.LICENSE_POLICY_EDIT],
    },
    vulnerabilityPolicy: {
      view: [...baseViewPermissions, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT],
      edit: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT],
      delete: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT],
    },
    assetClassification: {
      view: [...baseViewPermissions, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT],
      edit: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT],
      delete: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.VULNERABILITY_POLICY_EDIT],
    },
  },
  
  bulletin: {
    announcement: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      delete: baseDeletePermissions,
    },
    questionAndAnswer: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      delete: baseDeletePermissions,
    },
    faq: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      delete: baseDeletePermissions,
    },
    archive: {
      view: baseViewPermissions,
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER, LOGIN_CONTEXT.DEVELOPER],
      delete: baseDeletePermissions,
    },
  },

  product: {
    view: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR],
    add: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR],
    edit: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_MANAGER],
    delete: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR],
  },

  system: {
    user: {
      view: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER],
      edit: [...baseEditPermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER],
      delete: [...baseDeletePermissions, LOGIN_CONTEXT.PRODUCT_CREATOR, LOGIN_CONTEXT.PRODUCT_MANAGER, LOGIN_CONTEXT.PROJECT_MANAGER],
    },
    role: {
      view: [LOGIN_CONTEXT.SUPERUSER],
      edit: [LOGIN_CONTEXT.SUPERUSER],
      delete: [LOGIN_CONTEXT.SUPERUSER],
    },
    sbom: {
      view: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER, LOGIN_CONTEXT.SBOM_EDIT],
      edit: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.SBOM_EDIT],
      delete: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.SBOM_EDIT],
    },
    preferences: {
      view: [LOGIN_CONTEXT.SUPERUSER],
      edit: [LOGIN_CONTEXT.SUPERUSER],
      delete: [LOGIN_CONTEXT.SUPERUSER],
    },
    workHistory: {
      view: [LOGIN_CONTEXT.SUPERUSER, LOGIN_CONTEXT.MANAGER],
    }
  },
};
